import type { HttpQueryParam, IHttpClient } from "@/core/interfaces";
import { isAPIError, readServiceError } from "@/core/services";
import { extractDetail, extractItems, isRecord } from "@/core/utils";
import { appendReportQuery, parseContentDispositionFileName } from "@/modules/export";
import type { ExportedFile, ExportFormat } from "@/modules/export";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  AlocacaoDeLocal,
  ConflitoIgnorado,
  ConflitoRemanejado,
  IgnorarConflitoRequest,
  ListLocaisDisponiveisParams,
  LocalDisponivel,
  RemanejarLocalRequest,
  RemanejarLocalResult,
  AlocacaoDeObra,
  AlocacoesPorLocal,
  AlocacoesPorObra,
  ListAlocacoesParams,
  LocalComAlocacoes,
  LocalResumo,
  ObraComAlocacoes,
  ObraResumo,
  StatusAlocacao,
} from "./carga-de-trabalho.types";

export interface ExportAlocacoesLocalParams {
  dataInicio: string;
  dataFim: string;
  busca?: string;
  /** Rótulos dos filtros da tela, impressos no arquivo. */
  filtros?: string[];
  /** Valores do painel que o core aplica sobre o resultado. */
  painel?: Record<string, string | string[]>;
  format: ExportFormat;
  signal?: AbortSignal;
}

/** Contrato do hub-bff (privilégio `hub.equipe:view`, verificado pelo core). */
export const CARGA_DE_TRABALHO_PATHS = {
  LOCAIS: "/api/recursos/alocacoes/locais",
  OBRAS_LOCAIS: "/api/recursos/alocacoes/obras/locais",
  /** Locais para escolher o substituto (privilégio `hub.equipe:view`). */
  LOCAIS_DISPONIVEIS: "/api/recursos/locais",
  /** Ações de resolução do conflito (privilégio `hub.equipe:edit`). */
  REMANEJAMENTO: (alocacaoId: string) =>
    `/api/recursos/alocacoes/locais/${encodeURIComponent(alocacaoId)}/remanejamento`,
  CONFLITOS_IGNORADOS: "/api/recursos/alocacoes/locais/conflitos-ignorados",
} as const;

/** Código do 409 quando o local substituto foi ocupado entre a busca e a confirmação. */
export const LOCAL_INDISPONIVEL_CODE = "LOCAL_INDISPONIVEL";
export const LOCAL_INDISPONIVEL_MESSAGE =
  "Este local ficou indisponível para o período. Selecione outro local.";

/** Erro tipado: o local escolhido ficou indisponível para o período. */
export class LocalIndisponivelError extends Error {
  constructor() {
    super(LOCAL_INDISPONIVEL_MESSAGE);
    this.name = "LocalIndisponivelError";
  }
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function text(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toStatus(value: unknown): StatusAlocacao | null {
  const normalized = text(value).toUpperCase();
  return normalized === "PLANEJADO" || normalized === "REALIZADO" ? normalized : null;
}

function toObra(raw: unknown): ObraResumo | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  const codigo = text(raw.codigo);
  const nome = text(raw.nome);
  if (!id && !codigo && !nome) return null;

  const embarcacao = isRecord(raw.embarcacao)
    ? {
        id: text(raw.embarcacao.id),
        nome: text(raw.embarcacao.nome),
        acronimo: text(raw.embarcacao.acronimo) || null,
      }
    : null;

  return { id: id || codigo, codigo, nome, situacao: text(raw.situacao).toUpperCase(), embarcacao };
}

function toLocalResumo(raw: unknown): LocalResumo | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  const codigo = text(raw.codigo);
  const nome = text(raw.nome);
  if (!id && !codigo && !nome) return null;

  const cidade = text(raw.cidade);
  const uf = text(raw.uf).toUpperCase();

  return {
    id: id || codigo,
    codigo,
    nome,
    ...(cidade ? { cidade } : {}),
    ...(uf ? { uf } : {}),
  };
}

function toAlocacaoDeObra(raw: unknown, index: number): AlocacaoDeObra | null {
  if (!isRecord(raw)) return null;
  const obra = toObra(raw.obra);
  const inicio = text(raw.inicio);
  const fim = text(raw.fim);
  if (!obra || !inicio || !fim) return null;

  return {
    id: text(raw.id) || `${obra.id}-${index}`,
    inicio,
    fim,
    obra,
    status: toStatus(raw.status),
  };
}

function toAlocacaoDeLocal(raw: unknown, index: number): AlocacaoDeLocal | null {
  if (!isRecord(raw)) return null;
  const local = toLocalResumo(raw.local);
  const inicio = text(raw.inicio);
  const fim = text(raw.fim);
  if (!local || !inicio || !fim) return null;

  return {
    id: text(raw.id) || `${local.id}-${index}`,
    inicio,
    fim,
    local,
    status: toStatus(raw.status),
  };
}

function toLocal(raw: unknown): LocalComAlocacoes | null {
  const base = toLocalResumo(raw);
  if (!base || !isRecord(raw)) return null;

  return {
    ...base,
    alocacoes: list(raw.alocacoes)
      .map(toAlocacaoDeObra)
      .filter((item): item is AlocacaoDeObra => item !== null),
  };
}

function toObraComAlocacoes(raw: unknown): ObraComAlocacoes | null {
  const base = toObra(raw);
  if (!base || !isRecord(raw)) return null;

  return {
    ...base,
    alocacoes: list(raw.alocacoes)
      .map(toAlocacaoDeLocal)
      .filter((item): item is AlocacaoDeLocal => item !== null),
  };
}

function toConflitoIgnorado(raw: unknown): ConflitoIgnorado | null {
  if (!isRecord(raw)) return null;
  const localId = text(raw.localId);
  const conflitoInicio = text(raw.conflitoInicio);
  const conflitoFim = text(raw.conflitoFim);
  if (!localId || !conflitoInicio || !conflitoFim) return null;

  return {
    id: text(raw.id),
    localId,
    conflitoInicio,
    conflitoFim,
    alocacaoIds: list(raw.alocacaoIds).map(text).filter(Boolean),
  };
}

function toLocalDisponivel(raw: unknown): LocalDisponivel | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  const codigo = text(raw.codigo);
  const nome = text(raw.nome);
  if (!id && !codigo && !nome) return null;

  return {
    id: id || codigo,
    codigo,
    nome,
    cidade: text(raw.cidade),
    uf: text(raw.uf).toUpperCase(),
    disponibilidade:
      text(raw.disponibilidade).toUpperCase() === "DISPONIVEL" ? "DISPONIVEL" : "INDISPONIVEL",
  };
}

function toConflitoRemanejado(raw: unknown): ConflitoRemanejado | null {
  if (!isRecord(raw)) return null;

  return {
    localId: text(raw.localId),
    inicio: text(raw.inicio),
    fim: text(raw.fim),
    alocacaoIds: list(raw.alocacaoIds).map(text).filter(Boolean),
  };
}

function isLocalIndisponivel(error: unknown): boolean {
  return (
    isAPIError(error) &&
    error.status === 409 &&
    readServiceError(error).code === LOCAL_INDISPONIVEL_CODE
  );
}

function buildParams({ dataInicio, dataFim, busca }: ListAlocacoesParams) {
  const params: Record<string, string> = { dataInicio, dataFim };
  const term = busca?.trim();
  if (term) params.busca = term;
  return params;
}

/** Carga de trabalho agrupada por Local: obras alocadas por período + obras sem Local. */
export async function listAlocacoesPorLocal(
  request: ListAlocacoesParams,
): Promise<AlocacoesPorLocal> {
  const response = await getHttpClient().get<unknown>(CARGA_DE_TRABALHO_PATHS.LOCAIS, {
    signal: request.signal,
    params: buildParams(request),
  });
  const data = extractDetail(response.data);
  const record = isRecord(data) ? data : {};

  return {
    locais: list(record.locais)
      .map(toLocal)
      .filter((item): item is LocalComAlocacoes => item !== null),
    semLocal: list(record.semLocal)
      .map(toObra)
      .filter((item): item is ObraResumo => item !== null),
    conflitosIgnorados: list(record.conflitosIgnorados)
      .map(toConflitoIgnorado)
      .filter((item): item is ConflitoIgnorado => item !== null),
  };
}

/** Carga de trabalho agrupada por Obra: Locais alocados em cada obra. */
export async function listAlocacoesPorObra(
  request: ListAlocacoesParams,
): Promise<AlocacoesPorObra> {
  const response = await getHttpClient().get<unknown>(CARGA_DE_TRABALHO_PATHS.OBRAS_LOCAIS, {
    signal: request.signal,
    params: buildParams(request),
  });
  const data = extractDetail(response.data);
  const record = isRecord(data) ? data : {};

  return {
    obras: list(record.obras)
      .map(toObraComAlocacoes)
      .filter((item): item is ObraComAlocacoes => item !== null),
  };
}

/** Locais para substituir o do conflito, com a disponibilidade no período da obra. */
export async function listLocaisDisponiveis(
  request: ListLocaisDisponiveisParams,
): Promise<LocalDisponivel[]> {
  const params: Record<string, string> = {
    dataInicio: request.dataInicio,
    dataFim: request.dataFim,
  };
  if (request.ignorarObraId) params.ignorarObraId = request.ignorarObraId;
  const term = request.busca?.trim();
  if (term) params.busca = term;

  const response = await getHttpClient().get<unknown>(CARGA_DE_TRABALHO_PATHS.LOCAIS_DISPONIVEIS, {
    signal: request.signal,
    params,
  });

  return extractItems(response.data)
    .map(toLocalDisponivel)
    .filter((item): item is LocalDisponivel => item !== null);
}

/** Troca o Local da alocação; 409 `LOCAL_INDISPONIVEL` vira `LocalIndisponivelError`. */
export async function remanejarLocal(
  alocacaoId: string,
  body: RemanejarLocalRequest,
): Promise<RemanejarLocalResult> {
  try {
    const response = await getHttpClient().patch<unknown>(
      CARGA_DE_TRABALHO_PATHS.REMANEJAMENTO(alocacaoId),
      body,
    );
    const data = extractDetail(response.data);
    const record = isRecord(data) ? data : {};
    const alocacao = isRecord(record.alocacao) ? record.alocacao : {};

    return {
      alocacao: {
        id: text(alocacao.id),
        obraId: text(alocacao.obraId),
        localId: text(alocacao.localId),
        inicio: text(alocacao.inicio),
        fim: text(alocacao.fim),
      },
      localAnteriorId: text(record.localAnteriorId),
      novoConflito: record.novoConflito === true,
      conflitos: list(record.conflitos)
        .map(toConflitoRemanejado)
        .filter((item): item is ConflitoRemanejado => item !== null),
    };
  } catch (error) {
    if (isLocalIndisponivel(error)) throw new LocalIndisponivelError();
    throw error;
  }
}

/** Marca o conflito como tratado (201); 409 quando já estava registrado. */
export async function ignorarConflito(body: IgnorarConflitoRequest): Promise<void> {
  await getHttpClient().post<unknown>(CARGA_DE_TRABALHO_PATHS.CONFLITOS_IGNORADOS, body);
}

function buildExportParams({
  dataInicio,
  dataFim,
  busca,
  filtros,
  painel,
  format,
}: ExportAlocacoesLocalParams) {
  const params: Record<string, HttpQueryParam> = { dataInicio, dataFim, formato: format };
  const term = busca?.trim();
  if (term) params.busca = term;
  appendReportQuery(params, filtros, painel);
  return params;
}

/** Exportar Visualização — alocações agrupadas por local (US-2393). */
export async function exportAlocacoesPorLocal(
  request: ExportAlocacoesLocalParams,
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(
    `${CARGA_DE_TRABALHO_PATHS.LOCAIS}/exportacao`,
    { params: buildExportParams(request), signal: request.signal, responseType: "arraybuffer" },
  );
  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/** Exportar Visualização — alocações agrupadas por obra/local (US-2393). */
export async function exportAlocacoesLocalPorObra(
  request: ExportAlocacoesLocalParams,
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(
    `${CARGA_DE_TRABALHO_PATHS.OBRAS_LOCAIS}/exportacao`,
    { params: buildExportParams(request), signal: request.signal, responseType: "arraybuffer" },
  );
  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}
